import {
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import {
  ComboBox,
  ComboBoxOptionType,
  DropdownInput,
  DropdownOptionType,
  TooltipProps,
} from ".";
import { TAILWIND_COLORS } from "@/lib/tailwind";
import { SketchPicker } from "react-color";
import { CaretDownIcon, ResetIcon } from "@radix-ui/react-icons";
import React from "react";

const percFloatToHex = (i: number) => {
  const hex = Math.floor(i * 255).toString(16);
  return hex.length === 1 ? "0" + hex : hex;
};

// Convert a hex color to an RGB object
const hexToRgb = (hex: string) => {
  const bigint = parseInt(hex.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return { r, g, b };
};

// Compute the Euclidean distance between two RGB colors
const colorDistance = (
  color1: { r: number; g: number; b: number },
  color2: { r: number; g: number; b: number }
) => {
  return Math.sqrt(
    Math.pow(color1.r - color2.r, 2) +
      Math.pow(color1.g - color2.g, 2) +
      Math.pow(color1.b - color2.b, 2)
  );
};

// Compute the luminance for an RGB color
const getLuminance = (rgb: { r: number; g: number; b: number }) => {
  return 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
};

const getClosestColors = (
  baseColor: string,
  allColors: string[],
  count: number
) => {
  const baseRgb = hexToRgb(baseColor);
  return allColors
    .map((color) => ({
      color,
      distance: colorDistance(baseRgb, hexToRgb(color)),
      luminance: getLuminance(hexToRgb(color)),
    }))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, count)
    .sort((a, b) => a.luminance - b.luminance)
    .map((item) => item.color);
};

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  onUpdate: (color: string) => void;
  onDismiss: (color: string) => void;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  color,
  onUpdate,
  onChange,
  onDismiss,
}) => {
  const [internalValue, setInternalValue] = useState(color);
  const closestColors = getClosestColors(internalValue, TAILWIND_COLORS, 20);

  const handleColorChange = (color: any) => {
    setInternalValue(color.hex + percFloatToHex(color.rgb.a));
    onUpdate(color);
  };

  const handleColorChangeComplete = (color: any) => {
    onChange(color);
  };

  const handleClickAway = () => {
    onDismiss(internalValue);
  };

  return (
    <div className="absolute top-full left-0 z-[1000]">
      <div
        className="fixed top-0 right-0 bottom-0 left-0"
        onClick={() => {
          handleClickAway();
        }}
      />
      <SketchPicker
        color={internalValue || ""}
        onChange={handleColorChange}
        onChangeComplete={handleColorChangeComplete}
        presetColors={closestColors}
      />
    </div>
  );
};

interface ColorInputProps {
  value: string;
  evaluatedValue?: string;
  setValue: (val: string) => void;
  set: boolean;
  setSet: (set: boolean) => void;
  comboboxOptions?: ComboBoxOptionType[];
  onComboboxSelected?: (option: ComboBoxOptionType) => void;
  onValueUpdate?: (option: ComboBoxOptionType) => void; // Must be supplied with build arbitrary class fn
  dropdownOptions?: DropdownOptionType[];
  onDropdownSelected?: (option: DropdownOptionType) => void;
  buildArbitraryClass?: (input: string) => ComboBoxOptionType;
  buildOptionPreview?: (option: ComboBoxOptionType) => ReactNode;
  onInputFocus?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  disabled?: boolean;
  onClear?: () => void;
}

const ColorInput: React.FC<ColorInputProps & TooltipProps> = ({
  value,
  evaluatedValue,
  setValue,
  comboboxOptions,
  onComboboxSelected,
  onValueUpdate,
  buildArbitraryClass,
  buildOptionPreview,
  set = false,
  setSet,
  onInputFocus,
  disabled = false,
  tooltipContent = null,
  onClear,
  dropdownOptions = [],
  onDropdownSelected,
}) => {
  const [comboboxOpen, setComboboxOpen] = useState<null | boolean>(false);
  const [focused, setFocused] = useState(false);
  const [colorPickerVisible, setColorPickerVisible] = useState<boolean>(false);
  const [dropdownVisible, setDropdownVisible] = useState<null | boolean>(false);
  const [hovered, setHovered] = useState(false);

  const initialValueRef = useRef(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const onInputChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      setValue(ev.target.value || "");
      setComboboxOpen(true);
    },
    [setValue]
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") {
      return;
    }

    if (value === "" && onClear) {
      setComboboxOpen(false);
      onClear();

      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  const handleDropdownOptionClick = (option: DropdownOptionType) => {
    if (option.value === "clear" && onClear) {
      onClear();
    } else if (onDropdownSelected) {
      onDropdownSelected(option);
    }

    setDropdownVisible(false);
  };

  const enhancedDropdownOptions = useMemo(() => {
    if (onClear && set) {
      const clearDropdownOption: DropdownOptionType = {
        label: "Clear style",
        value: "clear",
        icon: <ResetIcon />,
      };

      // Return a new array with the 'Clear' option appended
      return [...dropdownOptions, clearDropdownOption];
    }

    return dropdownOptions;
  }, [dropdownOptions, onClear, set]);

  /**
   * Best attempt at extracting the first value from a provided CSS string
   *
   * Example:
   *
   * Input: padding-left: 2.25rem; // 36px \n padding-right: 2.25rem; // 36px
   * Output: 2.25rem
   *
   * @param css
   * @returns value
   */
  const safeExtractValueFromCss = (css: string): string | null => {
    try {
      const firstCss = css.split("\n")[0];
      let value: string = firstCss.split(":")[1];
      value = value.split(";")[0];

      return value.trim();
    } catch (error) {
      return null;
    }
  };

  /**
   * Track changes to disabled to ensure an unusable input isn't in focus.
   */
  useEffect(() => {
    if (disabled) {
      setFocused(false);
      setComboboxOpen(false);
    }
  }, [disabled]);

  const handleComboboxOptionClick = (option: ComboBoxOptionType) => {
    if (onComboboxSelected) {
      setSet(true);
      onComboboxSelected(option);
    }
    setComboboxOpen(false);

    const value = safeExtractValueFromCss(option.css);
    if (value) {
      setValue(value);
    }

    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  return (
    <>
      <div ref={colorInputRef}>
        {/*
         // @ts-expect-error TS2322 */}
        <div
          className={`flex flex-row items-center relative border bg-secondary-200 rounded-md h-8 px-2 ${
            focused
              ? "border-primary-400"
              : "hover:border-secondary-600 border-transparent"
          }`}
          data-tooltip-id="styling-panel-tooltip"
          data-tooltip-content={tooltipContent}
          data-tooltip-place="bottom"
          data-tooltip-offset={5}
          data-tooltip-hidden={comboboxOpen || dropdownVisible}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onMouseDown={(event) => {
            if (event.button === 2) {
              // Right click
              event.preventDefault();
            }
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            if (enhancedDropdownOptions && enhancedDropdownOptions.length > 0) {
              setDropdownVisible(true);
            }
          }}
        >
          <div
            className="flex flex-row py-2 px-2 cursor-pointer select-none"
            style={{ backgroundColor: evaluatedValue || value || "#fff" }}
            onClick={() => setColorPickerVisible(true)}
          ></div>
          <input
            ref={inputRef}
            disabled={disabled}
            value={value}
            onChange={onInputChange}
            onFocus={(event) => {
              event.target.select();
              setComboboxOpen(true);
              setFocused(true);

              if (onInputFocus) {
                onInputFocus(event);
              }
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              setFocused(false);
            }}
            className={`text-2xs px-1 border-none outline-none w-full font-medium bg-transparent ${
              disabled || !set ? "text-secondary-600" : "text-white"
            }`}
          />
          {enhancedDropdownOptions &&
            enhancedDropdownOptions.length > 0 &&
            hovered && (
              <div onClick={() => setDropdownVisible(true)}>
                <CaretDownIcon className="mr-1" />
              </div>
            )}
          <DropdownInput
            className="w-40"
            dropdownOptions={enhancedDropdownOptions}
            onDropdownSelected={handleDropdownOptionClick}
            // @ts-expect-error TS2322
            visible={dropdownVisible}
            setVisible={setDropdownVisible}
          />
          {colorPickerVisible && (
            <ColorPicker
              color={value}
              onChange={(color: any) => {
                const newValue = color.hex + percFloatToHex(color.rgb.a);
                setValue(newValue);
                if (
                  buildArbitraryClass &&
                  initialValueRef.current !== newValue
                ) {
                  // @ts-expect-error TS2722
                  onValueUpdate(buildArbitraryClass(newValue));
                }
              }}
              onUpdate={(color: any) => {
                const newValue = color.hex + percFloatToHex(color.rgb.a);
                setValue(newValue);
                if (
                  buildArbitraryClass &&
                  initialValueRef.current !== newValue
                ) {
                  // @ts-expect-error TS2722
                  onValueUpdate(buildArbitraryClass(newValue));
                }
              }}
              onDismiss={(color: string) => {
                setColorPickerVisible(false);
                if (buildArbitraryClass && initialValueRef.current !== color) {
                  // @ts-expect-error TS2722
                  onComboboxSelected(buildArbitraryClass(color));
                }
              }}
            />
          )}
        </div>
        {comboboxOpen && comboboxOptions && (
          <ComboBox
            input={(value || "").toString()}
            options={comboboxOptions}
            open={comboboxOpen}
            setOpen={setComboboxOpen}
            onOptionSelected={(option: ComboBoxOptionType) =>
              handleComboboxOptionClick(option)
            }
            buildOptionPreview={buildOptionPreview}
            buildArbitraryClass={buildArbitraryClass}
            anchorRef={colorInputRef}
          />
        )}
      </div>
    </>
  );
};

export default React.memo(ColorInput);
